import React, { useEffect, useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import Modal from 'react-responsive-modal'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { createGroupNutrition, updateGroupNutrition } from '../../actions/directoryCanteen'
import { Checkbox, Input } from '../../components/ModalInputs'
import Button from '../../components/Button'
import Confirmation from '../../components/Confirmation/Confirmation'
import styles from './styles.module.scss'

const PARALLELS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

const INITIAL_VALUES = {
  name: '',
  parallels: [],
  sort: '',
  xp_key: ''
}

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Обязательное поле')
    .max(30, 'Не более 30 символов'),
  sort: Yup.number()
    .typeError('Введите число')
    .required('Обязательное поле')
    .max(999999999, 'Не более 9 символов')
})

const GroupNutritionModal = ({
  open,
  onClose,
  data,
  addMode,
  parallels: parallelList
}) => {
  const dispatch = useDispatch()
  const [confirmation, setConfirmation] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const createItem = useCallback(async (values) => {
    setIsSubmitting(true)
    try {
      await dispatch(createGroupNutrition(values))
      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }, [dispatch, onClose])

  const updateItem = useCallback(async (values) => {
    setIsSubmitting(true)
    try {
      await dispatch(updateGroupNutrition(values))
      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }, [dispatch, onClose])

  const {
    values,
    errors,
    touched,
    dirty,
    submitCount,
    handleSubmit,
    handleChange,
    handleBlur,
    setValues,
    setFieldValue,
    resetForm
  } = useFormik({
    initialValues: INITIAL_VALUES,
    enableReinitialize: true,
    validationSchema,
    onSubmit: addMode ? createItem : updateItem
  })

  useEffect(() => {
    if (!open) {
      resetForm()
      return
    }
    setValues(addMode ? INITIAL_VALUES : data)
  }, [open, addMode, data, setValues, resetForm])

  const handleCloseModal = useCallback(() => {
    if (dirty) {
      setConfirmation(true)
    } else {
      onClose()
    }
  }, [dirty, onClose])

  const acceptConfirmation = useCallback(() => {
    onClose()
    setConfirmation(false)
  }, [onClose])

  const rejectConfirmation = useCallback(() => {
    setConfirmation(false)
  }, [])

  const getError = useCallback((fieldName) =>
    (touched[fieldName] || submitCount > 0) && errors[fieldName],
    [touched, errors, submitCount]
  )

  const handleChangeCheck = useCallback((e) => {
    const value = +e.target.value
    const currentParallels = values.parallels || []

    if (currentParallels.includes(value)) {
      setFieldValue('parallels', currentParallels.filter(el => el !== value))
    } else {
      setFieldValue('parallels', [...currentParallels, value])
    }
  }, [values.parallels, setFieldValue])

  const isParallelDisabled = useCallback((par) => {
    const currentParallels = values.parallels || []
    if (currentParallels.includes(par)) return false
    return parallelList?.includes(par)
  }, [values.parallels, parallelList])

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      classNames={{ modal: styles.modal }}
      closeOnEsc={!dirty}
      closeOnOverlayClick={!dirty}
      showCloseIcon={false}
      animationDuration={200}
      center
    >
      <form onSubmit={handleSubmit} className={styles.modalForm}>
        <div className={styles.modalMain}>
          <h2>{addMode ? 'Добавление группы питания' : 'Редактирование группы питания'}</h2>

          <Input
            label="Название"
            name="name"
            value={values.name || ''}
            error={getError('name')}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isSubmitting}
          />

          <Input
            label="Сортировка"
            name="sort"
            value={values.sort ?? ''}
            error={getError('sort')}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isSubmitting}
          />

          <div className={styles.parallelsSection}>
            <p className={styles.parallelsLabel}>Параллели</p>
            <div className={styles.parallelsGrid}>
              {PARALLELS.map(par => (
                <Checkbox
                  key={par}
                  label={par}
                  checked={values.parallels?.includes(par) || false}
                  value={par}
                  onChange={handleChangeCheck}
                  name="parallels"
                  disabled={isSubmitting || isParallelDisabled(par)}
                />
              ))}
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <div className={styles.footerInfo} />
          <div className={styles.footerButtons}>
            <Button
              mode="white"
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting || !dirty}
            >
              Сохранить
            </Button>
            <Button
              mode="white"
              type="button"
              onClick={handleCloseModal}
              disabled={isSubmitting}
            >
              Закрыть
            </Button>
          </div>
        </div>
      </form>

      {confirmation && (
        <Confirmation
          title="Есть несохраненные данные. Уверены что хотите выйти?"
          acceptConfirmation={acceptConfirmation}
          rejectConfirmation={rejectConfirmation}
        />
      )}
    </Modal>
  )
}

export default GroupNutritionModal
